import { Cartesian3, ConstantProperty, PolygonHierarchy, } from "cesium";
import { getDefaultSectionHighlightSetting, } from "../../settings/earthbrain-site-settings";
const SECTION_HIGHLIGHT_HEIGHT_MARGIN = 1;
export class SectionMarkerRenderer {
    _viewer;
    _site;
    _owner;
    _highlightSettings;
    _highlightEntity;
    _disposed = false;
    /** Incremented per call; stale async results are discarded. */
    _revision = 0;
    constructor(args) {
        this._viewer = args.viewer;
        this._site = args.site;
        this._owner = args.owner;
        this._highlightSettings = getDefaultSectionHighlightSetting();
    }
    /**
     * Rebuilds the non-interactive skeleton outline at the given (real 3D) distance from start,
     * showing which section is currently selected (AC-17).
     */
    async setSelectedDistance(distanceFromStart3D) {
        if (this._disposed)
            return;
        const revision = ++this._revision;
        const orientation = this._owner.getSectionOrientationAt3dDistance(distanceFromStart3D);
        if (!orientation) {
            this._removeHighlightEntity();
            return;
        }
        const { pointOnPath, direction } = orientation;
        const pathPoints = this._owner.getCrossSectionPathPoints(distanceFromStart3D);
        if (pathPoints.length === 0) {
            this._removeHighlightEntity();
            return;
        }
        const wgs84Corners = await this._site.convertPointsToWGS84(this._computeHighlightCorners(pointOnPath, direction, pathPoints));
        if (this._disposed || revision !== this._revision)
            return;
        if (this._highlightEntity) {
            this._highlightEntity.polygon.hierarchy = new ConstantProperty(new PolygonHierarchy(wgs84Corners));
            this._highlightEntity.show = true;
        }
        else {
            this._highlightEntity = this._createHighlightEntity(wgs84Corners);
        }
        this._viewer.scene.requestRender();
    }
    _computeHighlightCorners(pointOnPath, direction, pathPoints) {
        const right = direction.right;
        const lateralOffsets = pathPoints.map((p) => p[0]);
        const elevations = pathPoints.map((p) => p[1]);
        const minOffset = Math.min(...lateralOffsets);
        const maxOffset = Math.max(...lateralOffsets);
        // Margin so the panel doesn't get visually buried at the cross-section's highest point.
        const minZ = Math.min(...elevations) - SECTION_HIGHLIGHT_HEIGHT_MARGIN;
        const maxZ = Math.max(...elevations) + SECTION_HIGHLIGHT_HEIGHT_MARGIN;
        const left = Cartesian3.add(pointOnPath, Cartesian3.multiplyByScalar(right, minOffset, new Cartesian3()), new Cartesian3());
        const rightPoint = Cartesian3.add(pointOnPath, Cartesian3.multiplyByScalar(right, maxOffset, new Cartesian3()), new Cartesian3());
        const topLeft = new Cartesian3(left.x, left.y, maxZ);
        const topRight = new Cartesian3(rightPoint.x, rightPoint.y, maxZ);
        const bottomRight = new Cartesian3(rightPoint.x, rightPoint.y, minZ);
        const bottomLeft = new Cartesian3(left.x, left.y, minZ);
        return [topLeft, topRight, bottomRight, bottomLeft];
    }
    /** Removes the selection skeleton entity, if any. Terminal: later updates are ignored. */
    dispose() {
        this._disposed = true;
        this._removeHighlightEntity();
    }
    _createHighlightEntity(corners) {
        const entity = this._viewer.entities.add({
            polygon: {
                hierarchy: new PolygonHierarchy(corners),
                perPositionHeight: true,
                fill: true,
                material: this._highlightSettings.color,
                outline: true,
                outlineColor: this._highlightSettings.outlineColor,
                outlineWidth: this._highlightSettings.outlineWidth,
                show: true,
            },
        });
        entity.name = "sectionHighlight";
        // Non-interactive: consumed only by CesiumPicker.getAllowPickedEntity() / getPickablePickedInfo().
        entity.addProperty("allowPicking");
        entity["allowPicking"] = false;
        return entity;
    }
    _removeHighlightEntity() {
        if (this._highlightEntity) {
            this._viewer.entities.remove(this._highlightEntity);
            this._highlightEntity = undefined;
        }
    }
}
//# sourceMappingURL=section-marker-renderer.js.map